<template>
  <form
    class="flex flex-1 flex-col p-6 overflow-y-auto"
    @submit.prevent="onSubmit"
  >
    <div
      v-if="shouldShowHeaderMessage"
      v-dompurify-html="formatMessage(headerMessage, false)"
      class="mb-4 text-sm text-brand-text"
    />

    <!-- Dynamic pre-chat fields -->
    <template v-for="item in enabledPreChatFields">
      <!-- Text inputs: text, email, url, number -->
      <CsInput
        v-if="isTextInputType(item.type)"
        :key="`input-${item.name}`"
        :value="formData[item.name]"
        :name="item.name"
        :type="getHtmlInputType(item)"
        :label="getLabel(item)"
        :placeholder="getPlaceHolder(item)"
        :is-required="isContactFieldRequired(item.name)"
        :error="errors[item.name]"
        class="mb-4"
        @input="updateField(item.name, $event)"
        @blur="validateFieldOnBlur(item)"
      />

      <!-- Date picker -->
      <CsDatePicker
        v-else-if="item.type === 'date'"
        :key="`date-${item.name}`"
        :value="getDateValue(item.name)"
        :name="item.name"
        :label="getLabel(item)"
        :placeholder="getPlaceHolder(item)"
        :is-required="isContactFieldRequired(item.name)"
        :error="errors[item.name]"
        class="mb-4"
        @input="handleDateChange(item.name, $event)"
        @blur="validateFieldOnBlur(item)"
      />

      <!-- Select / Autocomplete -->
      <CsAutocomplete
        v-else-if="item.type === 'select'"
        :key="`select-${item.name}`"
        :value="getSelectValue(item)"
        :label="getLabel(item)"
        :placeholder="getPlaceHolder(item)"
        :suggestions="getSelectOptions(item)"
        :is-required="isContactFieldRequired(item.name)"
        :is-searchable="false"
        :error="errors[item.name]"
        class="mb-4"
        @input="handleSelectChange(item.name, $event)"
        @blur="validateFieldOnBlur(item)"
      />

      <!-- Checkbox -->
      <div
        v-else-if="item.type === 'checkbox'"
        :key="`checkbox-${item.name}`"
        class="mb-4 mt-2"
      >
        <CsCheckbox
          :value="!!formData[item.name]"
          @input="updateField(item.name, $event)"
        >
          {{ getLabel(item) }}
        </CsCheckbox>
      </div>

      <!-- Phone Input -->
      <CsWidgetPhoneInput
        v-else-if="item.type === 'phoneInput'"
        :key="`phone-${item.name}`"
        :value="formData[item.name]"
        :label="getLabel(item)"
        :placeholder="getPlaceHolder(item)"
        :is-required="isContactFieldRequired(item.name)"
        :error="errors[item.name]"
        class="mb-4"
        @input="updateField(item.name, $event)"
        @blur="validateFieldOnBlur(item)"
      />
    </template>

    <!-- Message textarea -->
    <CsTextarea
      :value="formData.message"
      name="message"
      :label="$t('PRE_CHAT_FORM.FIELDS.MESSAGE.LABEL')"
      :placeholder="$t('PRE_CHAT_FORM.FIELDS.MESSAGE.PLACEHOLDER')"
      :is-required="true"
      :error="errors.message"
      :min-height="128"
      class="mb-4"
      @input="updateField('message', $event)"
      @blur="validateMessageOnBlur"
    />

    <!-- Submit button -->
    <div class="flex w-full">
      <CsWidgetButton
        class="w-full"
        size="lg"
        :bg-color="widgetColor"
        :text-color="textColor"
        :is-disabled="isCreating || hasErrors"
        :is-loading="isCreating"
        type="submit"
      >
        {{ $t('START_CONVERSATION') }}
      </CsWidgetButton>
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import { getContrastingTextColor } from '@chatwoot/utils';
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import { isEmptyObject } from 'widget/helpers/utils';
import routerMixin from 'widget/mixins/routerMixin';
import darkModeMixin from 'widget/mixins/darkModeMixin';
import configMixin from 'widget/mixins/configMixin';
import {
  CsInput,
  CsTextarea,
  CsCheckbox,
  CsAutocomplete,
  CsDatePicker,
} from 'dashboard/components/ui/commslayer';
import CsWidgetPhoneInput from 'widget/v2/components/CsWidgetPhoneInput.vue';
import CsWidgetButton from 'widget/v2/components/CsWidgetButton.vue';
import { validateField } from 'widget/helpers/formValidation';

export default {
  components: {
    CsInput,
    CsTextarea,
    CsCheckbox,
    CsAutocomplete,
    CsDatePicker,
    CsWidgetPhoneInput,
    CsWidgetButton,
  },
  mixins: [routerMixin, darkModeMixin, messageFormatterMixin, configMixin],
  props: {
    options: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      locale: this.$root.$i18n.locale,
      formData: {},
      errors: {},
      touched: {},
      labels: {
        emailAddress: 'EMAIL_ADDRESS',
        fullName: 'FULL_NAME',
        phoneNumber: 'PHONE_NUMBER',
      },
    };
  },
  computed: {
    ...mapGetters({
      widgetColor: 'appConfig/getWidgetColor',
      isCreating: 'conversation/getIsCreating',
      activeCampaign: 'campaign/getActiveCampaign',
      currentUser: 'contacts/getCurrentUser',
    }),
    textColor() {
      return getContrastingTextColor(this.widgetColor);
    },
    hasActiveCampaign() {
      return !isEmptyObject(this.activeCampaign);
    },
    shouldShowHeaderMessage() {
      return this.hasActiveCampaign || this.preChatFormEnabled;
    },
    headerMessage() {
      if (this.preChatFormEnabled) {
        return this.options.preChatMessage;
      }
      if (this.hasActiveCampaign) {
        return this.$t('PRE_CHAT_FORM.CAMPAIGN_HEADER');
      }
      return '';
    },
    preChatFields() {
      return this.preChatFormEnabled ? this.options.preChatFields : [];
    },
    filteredPreChatFields() {
      const isUserEmailAvailable = !!this.currentUser.email;
      const isUserPhoneNumberAvailable = !!this.currentUser.phone_number;
      const isUserIdentifierAvailable = !!this.currentUser.identifier;
      const isUserNameAvailable = !!(
        isUserIdentifierAvailable ||
        isUserEmailAvailable ||
        isUserPhoneNumberAvailable
      );
      return this.preChatFields.filter(field => {
        if (isUserEmailAvailable && field.name === 'emailAddress') {
          return false;
        }
        if (isUserPhoneNumberAvailable && field.name === 'phoneNumber') {
          return false;
        }
        if (isUserNameAvailable && field.name === 'fullName') {
          return false;
        }
        return true;
      });
    },
    enabledPreChatFields() {
      return this.filteredPreChatFields
        .filter(field => field.enabled)
        .map(field => ({
          ...field,
          type:
            field.name === 'phoneNumber'
              ? 'phoneInput'
              : this.findFieldType(field.type),
        }));
    },
    conversationCustomAttributes() {
      let conversationAttributes = {};
      this.enabledPreChatFields.forEach(field => {
        if (field.field_type === 'conversation_attribute') {
          conversationAttributes = {
            ...conversationAttributes,
            [field.name]: this.getValue(field),
          };
        }
      });
      return conversationAttributes;
    },
    contactCustomAttributes() {
      let contactAttributes = {};
      this.enabledPreChatFields.forEach(field => {
        if (field.field_type === 'contact_attribute') {
          contactAttributes = {
            ...contactAttributes,
            [field.name]: this.getValue(field),
          };
        }
      });
      return contactAttributes;
    },
    hasErrors() {
      return Object.values(this.errors).some(e => !!e);
    },
    validationMessages() {
      return {
        required: this.$t('PRE_CHAT_FORM.REQUIRED'),
        email: this.$t('PRE_CHAT_FORM.FIELDS.EMAIL_ADDRESS.VALID_ERROR'),
        startsWithPlus: this.$t(
          'PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.DIAL_CODE_VALID_ERROR'
        ),
        isValidPhoneNumber: this.$t(
          'PRE_CHAT_FORM.FIELDS.PHONE_NUMBER.VALID_ERROR'
        ),
      };
    },
  },
  methods: {
    // Field update methods
    updateField(name, value) {
      this.$set(this.formData, name, value);
      // Validate on change if already touched
      if (this.touched[name]) {
        this.validateFieldByName(name);
      }
    },

    // Validation methods
    validateFieldByName(name) {
      const field = this.enabledPreChatFields.find(f => f.name === name);
      if (!field && name !== 'message') return;

      const rules =
        name === 'message' ? 'required' : this.getValidationRules(field);
      const error = validateField(
        this.formData[name],
        rules,
        this.validationMessages
      );
      this.$set(this.errors, name, error);
    },

    validateFieldOnBlur(item) {
      this.$set(this.touched, item.name, true);
      this.validateFieldByName(item.name);
    },

    validateMessageOnBlur() {
      this.$set(this.touched, 'message', true);
      this.validateFieldByName('message');
    },

    validateAllFields() {
      this.enabledPreChatFields.forEach(field => {
        this.$set(this.touched, field.name, true);
        this.validateFieldByName(field.name);
      });
      this.$set(this.touched, 'message', true);
      this.validateFieldByName('message');

      return !this.hasErrors;
    },

    // Field type helpers
    isTextInputType(type) {
      return ['text', 'url', 'number', 'email'].includes(type);
    },

    getHtmlInputType(item) {
      const { type } = item;
      const typeMap = { url: 'url', number: 'number', email: 'email' };
      return typeMap[type] || 'text';
    },

    // Select field helpers
    getSelectOptions(item) {
      if (!item.values) return [];
      return item.values.map((value, index) => ({
        id: index,
        name: value,
      }));
    },

    getSelectValue(item) {
      const value = this.formData[item.name];
      if (value === undefined || value === null) return {};
      return {
        id: value,
        name: item.values?.[value] || '',
      };
    },

    handleSelectChange(name, selected) {
      this.updateField(name, selected?.id);
    },

    // Date field helpers
    getDateValue(name) {
      const value = this.formData[name];
      if (!value) return null;
      if (value instanceof Date) return value;
      return new Date(value);
    },

    handleDateChange(name, date) {
      this.updateField(name, date);
    },

    // Existing helper methods
    isContactFieldRequired(fieldName) {
      const field = this.preChatFields.find(
        option => option.name === fieldName
      );
      return field?.required || false;
    },

    getLabel({ label }) {
      return label;
    },

    getPlaceHolder({ placeholder }) {
      return placeholder;
    },

    getValue({ name, type }) {
      if (type === 'select') {
        const field = this.enabledPreChatFields.find(
          option => option.name === name
        );
        return field?.values?.[this.formData[name]] || null;
      }
      return this.formData[name] || null;
    },

    getValidationRules({ type, name }) {
      const validationsByType = {
        email: 'email',
        phoneInput: 'startsWithPlus|isValidPhoneNumber',
        url: 'url',
        date: 'date',
      };

      const isRequired = this.isContactFieldRequired(name);
      const baseValidation = isRequired ? 'required' : 'optional';
      const typeValidation = validationsByType[type];

      if (typeValidation) {
        return `${baseValidation}|${typeValidation}`;
      }

      return isRequired ? 'required' : '';
    },

    findFieldType(type) {
      if (type === 'link') return 'url';
      if (type === 'list') return 'select';
      return type;
    },

    onSubmit() {
      if (!this.validateAllFields()) return;

      const { emailAddress, fullName, phoneNumber, message } = this.formData;
      const { email } = this.currentUser;
      this.$emit('submit', {
        fullName,
        phoneNumber,
        emailAddress: emailAddress || email,
        message,
        activeCampaignId: this.activeCampaign.id,
        conversationCustomAttributes: this.conversationCustomAttributes,
        contactCustomAttributes: this.contactCustomAttributes,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~widget/assets/scss/variables.scss';

::v-deep {
  .pre-chat-header-message {
    .link {
      color: $color-woot;
      text-decoration: underline;
    }
  }
}
</style>
